import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Tooltip, Select } from 'antd';
import { FaChevronLeft } from 'react-icons/fa';
import { FiPlus } from 'react-icons/fi';
import ApiClient from '../../methods/api/apiClient';
import loader from '../../methods/loader';
import Layout from '../../components/global/layout';
import shared from './shared';
const { Option } = Select;

const OFFER_TYPES = [
  { value: "percent_discount", label: "Percentage Off" },
  { value: "flat_discount", label: "Flat Discount" },
  { value: "fixed_discount", label: "Fixed Discount" },
  { value: "points_discount", label: "Points Discount" },
  { value: "earn_multiplier", label: "Points Multiplier" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "expired", label: "Expired" },
];

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function AddEditOffer() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    venueId: '',
    title: '',
    description: '',
    type: 'percent_discount',
    value: '',
    pointsRequired: 0,
    minSpend: '',
    maxDiscount: '',
    usageLimitPerUser: '',
    totalUsageLimit: '',
    validFrom: '',
    validTill: '',
    daysOfWeek: [],
    timeWindowStart: '',
    timeWindowEnd: '',
    status: 'active',
  });

  const [venues, setVenues] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getVenues();
    if (id) {
      getDetail();
    }
  }, [id]);

  const getVenues = () => {
    ApiClient.get('venue/listing', { page: 1, count: 1000 }).then(res => {
      if (res.success) {
        setVenues(res.data || []);
      }
    });
  };

  const getDetail = () => {
    loader(true);
    ApiClient.get(shared.detailApi, { id }).then(res => {
      if (res.success) {
        const d = res.data;
        setForm({
          venueId: d.venueId?._id || d.venueId?.id || d.venueId || '',
          title: d.title || '',
          description: d.description || '',
          type: d.type || 'percent_discount',
          value: d.value || '',
          pointsRequired: d.pointsRequired || 0,
          minSpend: d.minSpend || '',
          maxDiscount: d.maxDiscount || '',
          usageLimitPerUser: d.usageLimitPerUser || '',
          totalUsageLimit: d.totalUsageLimit || '',
          validFrom: d.validFrom ? new Date(d.validFrom).toISOString().split('T')[0] : '',
          validTill: d.validTill ? new Date(d.validTill).toISOString().split('T')[0] : '',
          daysOfWeek: d.daysOfWeek || [],
          timeWindowStart: d.timeWindow?.start || '',
          timeWindowEnd: d.timeWindow?.end || '',
          status: d.status || 'active',
        });
      }
      loader(false);
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (!form.title || !form.venueId || !form.validFrom || !form.validTill) {
      return;
    }

    const payload = {
      ...form,
      validFrom: new Date(form.validFrom).toISOString(),
      validTill: new Date(form.validTill + "T23:59:59.000Z").toISOString(),
      timeWindow: {
        start: form.timeWindowStart,
        end: form.timeWindowEnd
      }
    };

    if (id) payload.id = id;

    loader(true);
    const method = id ? 'put' : 'post';
    const url = id ? shared.editApi : shared.addApi;

    ApiClient.allApi(url, payload, method).then(res => {
      loader(false);
      if (res.success) {
        navigate(`/${shared.url}`);
      }
    });
  };

  const toggleDay = (day) => {
    setForm(prev => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day]
    }));
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex gap-3 mb-8">
          <Tooltip title="Back">
            <Link to={`/${shared.url}`} className="w-8 h-8 rounded-full bg-[#CC9900] text-white flex items-center justify-center hover:opacity-80">
              <FaAngleLeft />
            </Link>
          </Tooltip>
          <div>
            <h3 className="text-2xl font-bold text-[#221E10]">{id ? 'Edit' : 'Add'} Offer</h3>
            <p className="text-sm text-gray-500">Promote your venues with unique offers</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Main Info */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-5">
            <h4 className="font-bold text-gray-800 border-b pb-3 mb-4">Offer Details</h4>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Select Venue <span className="text-red-500">*</span></label>
              <Select
                className="w-full"
                placeholder="Select a venue"
                value={form.venueId || undefined}
                onChange={(v) => setForm({ ...form, venueId: v })}
                showSearch
                optionFilterProp="children"
              >
                {venues.map(v => (
                  <Option key={v._id || v.id} value={v._id || v.id}>{v.name}</Option>
                ))}
              </Select>
              {submitted && !form.venueId && <p className="text-red-500 text-xs mt-1">Venue is required</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Offer Title <span className="text-red-500">*</span></label>
              <input
                type="text"
                className="w-full h-11 px-4 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-[#CC9900] transition-all outline-none"
                placeholder="e.g. 20% Off Weekend"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
              {submitted && !form.title && <p className="text-red-500 text-xs mt-1">Title is required</p>}
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description</label>
                <textarea
                    className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-[#CC9900] transition-all outline-none resize-none"
                    rows={4}
                    placeholder="Enter offer description..."
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
               <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Type</label>
                  <Select
                    className="w-full"
                    value={form.type}
                    onChange={(v) => setForm({ ...form, type: v })}
                  >
                    {OFFER_TYPES.map(t => (
                      <Option key={t.value} value={t.value}>{t.label}</Option>
                    ))}
                  </Select>
               </div>
               <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Status</label>
                  <Select
                    className="w-full"
                    value={form.status}
                    onChange={(v) => setForm({ ...form, status: v })}
                  >
                    {STATUS_OPTIONS.map(s => (
                      <Option key={s.value} value={s.value}>{s.label}</Option>
                    ))}
                  </Select>
               </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Value</label>
                  <input
                    type="number"
                    className="w-full h-11 px-4 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-[#CC9900] transition-all outline-none"
                    placeholder="0"
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Points Required</label>
                  <input
                    type="number"
                    className="w-full h-11 px-4 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-[#CC9900] transition-all outline-none"
                    placeholder="0"
                    value={form.pointsRequired}
                    onChange={(e) => setForm({ ...form, pointsRequired: e.target.value })}
                  />
                </div>
            </div>
          </div>

          {/* Limits & Timing */}
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <h4 className="font-bold text-gray-800 border-b pb-3 mb-4">Validity & Usage</h4>
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Valid From <span className="text-red-500">*</span></label>
                        <input
                            type="date"
                            className="w-full h-11 px-4 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-[#CC9900] transition-all outline-none"
                            value={form.validFrom}
                            onChange={(e) => setForm({ ...form, validFrom: e.target.value })}
                        />
                        {submitted && !form.validFrom && <p className="text-red-500 text-xs mt-1">Required</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Valid Till <span className="text-red-500">*</span></label>
                        <input
                            type="date"
                            className="w-full h-11 px-4 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-[#CC9900] transition-all outline-none"
                            value={form.validTill}
                            onChange={(e) => setForm({ ...form, validTill: e.target.value })}
                        />
                        {submitted && !form.validTill && <p className="text-red-500 text-xs mt-1">Required</p>}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Usability / User</label>
                        <input
                            type="number"
                            className="w-full h-11 px-4 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-[#CC9900] transition-all outline-none"
                            placeholder="Unlimited"
                            value={form.usageLimitPerUser}
                            onChange={(e) => setForm({ ...form, usageLimitPerUser: e.target.value })}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Total Limit</label>
                        <input
                            type="number"
                            className="w-full h-11 px-4 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-[#CC9900] transition-all outline-none"
                            placeholder="Unlimited"
                            value={form.totalUsageLimit}
                            onChange={(e) => setForm({ ...form, totalUsageLimit: e.target.value })}
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <h4 className="font-bold text-gray-800 border-b pb-3 mb-4">Schedule</h4>
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Start Time</label>
                        <input
                            type="time"
                            className="w-full h-11 px-4 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-[#CC9900] transition-all outline-none"
                            value={form.timeWindowStart}
                            onChange={(e) => setForm({ ...form, timeWindowStart: e.target.value })}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">End Time</label>
                        <input
                            type="time"
                            className="w-full h-11 px-4 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-[#CC9900] transition-all outline-none"
                            value={form.timeWindowEnd}
                            onChange={(e) => setForm({ ...form, timeWindowEnd: e.target.value })}
                        />
                    </div>
                </div>

                <label className="block text-sm font-semibold text-gray-700 mb-2.5">Available Days</label>
                <div className="flex flex-wrap gap-2">
                    {DAY_LABELS.map((day, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => toggleDay(i)}
                            className={`w-10 h-10 rounded-lg text-xs font-bold transition-all border ${form.daysOfWeek.includes(i) 
                                ? 'bg-[#CC9900] text-white border-[#CC9900] shadow-sm' 
                                : 'bg-gray-50 text-gray-500 border-gray-100 hover:border-[#CC9900]'}`}
                        >
                            {day}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex gap-4 pt-4">
                <button
                    type="submit"
                    className="flex-1 bg-[#CC9900] text-white h-12 rounded-xl font-bold hover:opacity-90 transition-all shadow-lg shadow-[#CC9900]/20"
                >
                    {id ? 'Update' : 'Publish'} Offer
                </button>
                <Link
                    to={`/${shared.url}`}
                    className="flex-1 bg-gray-100 text-gray-600 h-12 rounded-xl font-bold flex items-center justify-center hover:bg-gray-200 transition-all"
                >
                    Cancel
                </Link>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
}

// Simple back icon component if FaAngleLeft is not available or similar
const FaAngleLeft = () => (
    <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 256 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
        <path d="M31.7 239l136-136c9.4-9.4 24.6-9.4 33.9 0l22.6 22.6c9.4 9.4 9.4 24.6 0 33.9L127.9 256l96.4 96.4c9.4 9.4 9.4 24.6 0 33.9L201.7 409c-9.4 9.4-24.6 9.4-33.9 0l-136-136c-9.5-9.4-9.5-24.6-.1-34z"></path>
    </svg>
);

export default AddEditOffer;
